<template>
  <div class="png-sprite-icon" :class="{ disabled: disabled }" :style="spriteStyle" :title="title" />
</template>

<script>
import colors from "@/assets/colors.js";

export default {
  name: "PngSpriteIcon",

  props: {
    icon: {
      type: String,
      required: true
    },
    sprite: {
      type: String,
      required: true
    },
    spriteSheetWidth: {
      type: Number,
      default: 2048
    },
    spriteSheetHeight: {
      type: Number,
      default: 2048
    },
    width: {
      type: [Number, String],
      default: 24
    },
    height: {
      type: [Number, String],
      default: 24
    },
    disabled: {
      type: Boolean,
      default: false
    },
    color: {
      type: String,
      default: null
    },
    title: {
      type: String,
      default: ""
    }
  },

  computed: {
    spriteData() {
      try {
        // Assuming your sprite files are in 'src/assets/sprites'
        return require(`@/assets/sprites/${this.sprite}.json`);
      } catch (error) {
        console.warn(`Sprite data not found: ${this.sprite}.json`);
        return null;
      }
    },

    spriteImage() {
      try {
        return require(`@/assets/sprites/${this.sprite}.png`);
      } catch (error) {
        console.warn(`Sprite image not found: ${this.sprite}.png`);
        return null;
      }
    },

    iconData() {
      if (!this.spriteData) {
        return null;
      }

      const iconData = this.spriteData[this.icon] || this.spriteData.generic;
      return iconData;
    },

    spriteStyle() {
      if (!this.iconData || !this.spriteImage) {
        return {};
      }

      // Get the icon coordinates and dimensions from JSON
      const { x, y, width, height } = this.iconData;

      // Calculate the display size
      const displayWidth = this.width;
      const displayHeight = this.height;

      // The JSON coordinates are for a smaller sprite sheet, but the actual PNG is 2048x2048
      // We need to scale the coordinates to match the actual sprite sheet size
      const jsonSpriteWidth = 2048; // Based on max x coordinate + icon width
      const jsonSpriteHeight = 160; // Based on max y coordinate + icon height

      // Calculate the scale factor to convert JSON coordinates to actual sprite sheet coordinates
      const coordScaleX = this.spriteSheetWidth / jsonSpriteWidth;
      const coordScaleY = this.spriteSheetHeight / jsonSpriteHeight;

      // Scale the coordinates to match the actual sprite sheet
      const actualX = x * coordScaleX;
      const actualY = y * coordScaleY;
      const actualWidth = width * coordScaleX;
      const actualHeight = height * coordScaleY;

      // Calculate the display scale factors
      const scaleX = displayWidth / actualWidth;
      const scaleY = displayHeight / actualHeight;

      // Calculate the scaled sprite sheet dimensions
      const scaledSheetWidth = this.spriteSheetWidth * scaleX;
      const scaledSheetHeight = this.spriteSheetHeight * scaleY;

      // Calculate the scaled position
      const scaledX = actualX * scaleX;
      const scaledY = actualY * scaleY;

      const style = {
        width: `${displayWidth}px`,
        height: `${displayHeight}px`,
        backgroundImage: `url(${this.spriteImage})`,
        backgroundSize: `${scaledSheetWidth}px ${scaledSheetHeight}px`,
        backgroundPosition: `-${scaledX}px -${scaledY}px`,
        backgroundRepeat: "no-repeat",
        display: "inline-block",
        verticalAlign: "middle"
      };

      // Apply filters for visibility
      if (this.disabled) {
        style.filter = "grayscale(1) opacity(0.5)";
      } else if (this.color) {
        const parsedColor = this.parseColorValue(this.color);
        if (parsedColor !== "#9095A2") {
          style.filter = this.createColorFilter(parsedColor);
        } else {
          style.filter = "invert(1) brightness(0.8)";
        }
      } else {
        // Default filter to make dark icons visible on light backgrounds
        style.filter = "invert(1) brightness(0.8)";
      }

      return style;
    }
  },

  methods: {
    parseColorValue(colorString) {
      if (colorString.startsWith("#")) {
        return colorString;
      }
      const [palette, variant] = colorString.split("-");
      const generatedColors = colors();
      if (generatedColors[palette]) {
        if (generatedColors[palette][variant]) {
          return generatedColors[palette][variant];
        }
        if (!variant) {
          return generatedColors[palette].base;
        }
      }
      return colorString;
    },

    createColorFilter(hexColor) {
      const hex = hexColor.replace("#", "");
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);

      // A slightly simplified but effective filter for colorizing an inverted monochrome icon
      const hue = this.getHueFromRGB(r, g, b);
      return `invert(1) sepia(1) saturate(500%) hue-rotate(${hue}deg)`;
    },

    getHueFromRGB(r, g, b) {
      r /= 255;
      g /= 255;
      b /= 255;

      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      let h = 0;

      if (max !== min) {
        const d = max - min;
        switch (max) {
          case r:
            h = (g - b) / d + (g < b ? 6 : 0);
            break;
          case g:
            h = (b - r) / d + 2;
            break;
          case b:
            h = (r - g) / d + 4;
            break;
        }
        h /= 6;
      }

      return h * 360;
    }
  }
};
</script>

<style lang="scss">
.png-sprite-icon {
  user-select: none;
  // The filter for the disabled state is now applied as an inline style
  // to avoid specificity conflicts.
}

// NOTE: This .selected rule will likely NOT work as intended because the
// inline style's filter will override it. For a selected state, you
// should add a `selected` prop to the component and handle its
// filter logic inside the `spriteStyle` computed property.
.selected {
  .png-sprite-icon {
    filter: brightness(0) invert(1);
  }
}
</style>
